import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, orderBy, limit, getDocs, startAt, endAt } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getUserData } from '../pages/auth/utils/firebase/userOperations';
import { useSelector } from 'react-redux';
import { IconX, IconSearch } from '@tabler/icons-react';
import './FriendSearchModal.css';

/**
 * FriendSearchModal Component
 * Search and select users/friends for sharing
 * Supports global search and suggestions
 */
const FriendSearchModal = ({ isOpen, selectedFriends = [], onToggleSelection, onClose }) => {
    const currentUser = useSelector(state => state.auth.currentUser);
    const [searchTerm, setSearchTerm] = useState('');
    const [defaultUsers, setDefaultUsers] = useState([]); // Recent chats + suggestions
    const [searchResults, setSearchResults] = useState([]); // Active search results
    const [loading, setLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // Fetch default users (recent chats + suggestions) on mount
    useEffect(() => {
        const fetchDefaultUsers = async () => {
            if (!currentUser?.uid || !isOpen) return;

            try {
                setLoading(true);

                // 1. Get recent conversations
                const conversationsRef = collection(db, 'conversations');
                const q = query(
                    conversationsRef,
                    where('memberIds', 'array-contains', currentUser.uid),
                    orderBy('lastMessageAt', 'desc'),
                    limit(20)
                );

                const snapshot = await getDocs(q);
                const conversationsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const userIdsSet = new Set();
                conversationsList.forEach(conv => {
                    if (Array.isArray(conv.memberIds)) {
                        conv.memberIds.forEach(id => {
                            if (id !== currentUser.uid) userIdsSet.add(id);
                        });
                    }
                });

                // 2. Fetch user data for friends
                const friendIds = Array.from(userIdsSet).slice(0, 10);
                const friendsData = await Promise.all(
                    friendIds.map(id => getUserData(id).catch(() => null))
                );
                let validUsers = friendsData.filter(u => u !== null);

                // 3. If few friends, fetch suggestions (use SAME logic as search since it works)
                if (validUsers.length < 10) {
                    try {
                        const usersRef = collection(db, 'users');
                        let suggestions = [];

                        // Use the EXACT same strategy as the working search
                        try {
                            // Try orderBy first (this is what works for search)
                            const q = query(usersRef, orderBy('username_lower'), limit(50));
                            const snap = await getDocs(q);
                            suggestions = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        } catch (e) {
                            console.warn('username_lower query failed, using fallback:', e);
                            // Fallback: get 100 users and filter client-side (same as search fallback)
                            const fallbackQ = query(usersRef, limit(100));
                            const fallbackSnap = await getDocs(fallbackQ);
                            suggestions = fallbackSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        }

                        // Add unique suggestions (filter out current user)
                        for (const user of suggestions) {
                            if (validUsers.length >= 20) break;
                            if (user.id !== currentUser.uid && !validUsers.find(u => u.id === user.id)) {
                                validUsers.push(user);
                            }
                        }
                    } catch (err) {
                        console.error('Error fetching suggestions:', err);
                    }
                }

                setDefaultUsers(validUsers);
            } catch (error) {
                console.error('Error fetching default users:', error);
                setDefaultUsers([]);
            } finally {
                setLoading(false);
            }
        };

        fetchDefaultUsers();
    }, [currentUser?.uid, isOpen]);

    // Handle Search
    useEffect(() => {
        const handleSearch = async () => {
            const term = searchTerm.trim().toLowerCase();
            if (term.length < 2) {
                setIsSearching(false);
                setSearchResults([]);
                return;
            }

            setIsSearching(true);
            setLoading(true);

            try {
                const usersRef = collection(db, 'users');
                let foundUsers = [];

                // Strategy 1: username_lower prefix search
                try {
                    const q = query(
                        usersRef,
                        orderBy('username_lower'),
                        startAt(term),
                        endAt(term + '\uf8ff'),
                        limit(20)
                    );
                    const snap = await getDocs(q);
                    foundUsers = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                } catch (e) {
                    console.warn('Prefix search failed, falling back:', e);
                }

                // Strategy 2: Fallback to client-side filter of recent users if prefix search empty
                if (foundUsers.length === 0) {
                    const fallbackQ = query(usersRef, limit(100));
                    const fallbackSnap = await getDocs(fallbackQ);
                    const allUsers = fallbackSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                    foundUsers = allUsers.filter(user => {
                        const name = (user.name || user.displayName || '').toLowerCase();
                        const username = (user.username || '').toLowerCase();
                        return name.includes(term) || username.includes(term);
                    });
                }

                // Filter out current user
                setSearchResults(foundUsers.filter(u => u.id !== currentUser.uid));
            } catch (error) {
                console.error('Search failed:', error);
                setSearchResults([]);
            } finally {
                setLoading(false);
            }
        };

        const timeoutId = setTimeout(handleSearch, 300); // Debounce
        return () => clearTimeout(timeoutId);
    }, [searchTerm, currentUser?.uid]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    // Lock body scroll
    useEffect(() => {
        if (!isOpen) return;
        const scrollY = window.scrollY || window.pageYOffset;
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';

        const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKeyDown);

        return () => {
            window.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.width = '';
            window.scrollTo(0, scrollY);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const displayUsers = isSearching ? searchResults : defaultUsers;
    const showSuggestionsLabel = !isSearching && defaultUsers.length > 0;

    return createPortal(
        (
            <div
                className="friend-search-overlay"
                onClick={handleBackdropClick}
                role="dialog"
                aria-modal="true"
                aria-label="Search and select friends"
            >
                <div className="friend-search-modal">
                    {/* Header */}
                    <div className="friend-search-header">
                        <h2>Select Friends</h2>
                        <button className="friend-search-close" onClick={onClose}>
                            <IconX size={20} />
                        </button>
                    </div>

                    {/* Search Input */}
                    <div className="friend-search-input-wrapper">
                        <div className="friend-search-input-inner">
                            <IconSearch size={20} className="search-icon" />
                            <input
                                type="text"
                                className="friend-search-input"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* User List */}
                    <div className="friend-search-results">
                        {loading ? (
                            <div className="friend-search-loading">Loading...</div>
                        ) : displayUsers.length === 0 ? (
                            <div className="friend-search-empty">
                                {isSearching ? `No users found matching "${searchTerm}"` : 'No users found'}
                            </div>
                        ) : (
                            <>
                                {showSuggestionsLabel && (
                                    <div className="friend-search-section-label">Suggested</div>
                                )}
                                {displayUsers.map(user => {
                                    const userId = user.id || user.uid;
                                    const isSelected = selectedFriends.includes(userId);
                                    const displayName = user.displayName || user.username || user.name || 'User';
                                    const profession = user.userProfession || user.profession || '';
                                    const photoURL = user.photoURL || user.userPhotoURL || null;

                                    return (
                                        <button
                                            key={userId}
                                            type="button"
                                            className={`friend-search-item ${isSelected ? 'friend-search-item--selected' : ''}`}
                                            onClick={() => onToggleSelection(userId)}
                                        >
                                            <div className="friend-search-avatar">
                                                {photoURL ? (
                                                    <img src={photoURL} alt={displayName} />
                                                ) : (
                                                    <div className="friend-search-avatar-placeholder">
                                                        {displayName.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="friend-search-info">
                                                <div className="friend-search-name">{displayName}</div>
                                                {profession && (
                                                    <div className="friend-search-profession">{profession}</div>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <div className="friend-search-check">✓</div>
                                            )}
                                        </button>
                                    );
                                })}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="friend-search-footer">
                        <div className="friend-search-count">
                            {selectedFriends.length} selected
                        </div>
                        <button className="friend-search-done-btn" onClick={onClose}>
                            Done
                        </button>
                    </div>
                </div>
            </div>
        ),
        document.body
    );
};

export default FriendSearchModal;
